import { useState, useEffect } from "react";
import { Box, Slider, Typography, Paper, Button } from "@mui/material";
import axios from "axios";
import ImageIcon from "@mui/icons-material/Image";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ImageBrowserPopup from "../PreviousImagePopup/popup"; // Adjust the path as needed

export default function EyelinerApp() {
  const [image, setImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [wingScale, setWingScale] = useState(30);
  const [fillIntensity, setFillIntensity] = useState(50);
  const [skipFraction, setSkipFraction] = useState(20);
  const [color, setColor] = useState({ r: 0, g: 0, b: 0 });
  const [scanning, setScanning] = useState(false);
  const [imageBrowserOpen, setImageBrowserOpen] = useState(false);

  // Updated common button style for consistency with HairColorApp
  const commonButtonStyle = {
    textTransform: "none",
    borderRadius: "8px",
    padding: "12px 20px",
    minWidth: "160px",
    fontSize: "16px",
    width: "250px",
  };

  // Removed useEffect auto-processing so that the makeup only applies on clicking the button
  useEffect(() => {
    const storedImageUrl = localStorage.getItem("selectedImageUrl");
    if (storedImageUrl && !image) {
      handlePreviousImageSelect({ url: storedImageUrl });
    }
    // eslint-disable-next-line
  }, []);

  // Handle image selection from popup
  const handlePreviousImageSelect = async (selectedImage) => {
    setScanning(true);
    try {
      localStorage.setItem("selectedImageUrl", selectedImage.url);
      const response = await fetch(selectedImage.url);
      const blob = await response.blob();
      const file = new File([blob], "selected_image.jpg", {
        type: "image/jpeg",
      });
      setProcessedImage(URL.createObjectURL(file));
      setImage(file);
    } catch (error) {
      console.error("Error processing previous image:", error);
      toast.error("Error processing previous image.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
    setImageBrowserOpen(false);
    setScanning(false);
  };

  // Process the image using the eyeliner API
  const processImage = async (file) => {
    if (!file) {
      toast.error("Please select an image before applying makeup.", {
        position: "bottom-center",
        autoClose: 3000,
      });
      return;
    }
    setScanning(true);
    try {
      // Step 1: Upload file to get an intermediate preview
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      const uploadResponse = await axios.post(
        `${process.env.REACT_APP_API_KEY}/makeup/upload`,
        formDataUpload,
        {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        }
      );
      if (!uploadResponse.data) {
        throw new Error("Upload API did not return image data");
      }
      const intermediateImageUrl = URL.createObjectURL(uploadResponse.data);
      setProcessedImage(intermediateImageUrl);

      // Delay for scanning animation
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Step 2: Apply eyeliner using the API with provided parameters
      const formData = new FormData();
      formData.append("file", file);
      formData.append("wing_scale", (wingScale / 100).toString());
      formData.append("fill_intensity", (fillIntensity / 100).toString());
      formData.append("skip_inner_fraction", (skipFraction / 100).toString());
      formData.append("r", color.r.toString());
      formData.append("g", color.g.toString());
      formData.append("b", color.b.toString());

      const response = await axios.post(
        `${process.env.REACT_APP_API_KEY}/makeup/apply-eyeliner`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        }
      );
      if (!response.data) {
        throw new Error("Eyeliner API did not return image data");
      }
      setProcessedImage(URL.createObjectURL(response.data));
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Error processing image. Please try another image.", {
        position: "bottom-center",
        autoClose: 3000,
      });
    }
    setScanning(false);
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 4,
        p: 4,
        pt: 16,
        pb: 16,
        minHeight: "100vh",
        width: "100%",
        bgcolor: "#0d1117",
        fontFamily: "Roboto, sans-serif",
        color: "#fff",
      }}
    >
      {/* Image Browser Popup */}
      <ImageBrowserPopup
        open={imageBrowserOpen}
        onClose={() => setImageBrowserOpen(false)}
        onImageSelect={handlePreviousImageSelect}
      />

      {/* Processed Image Display */}
      <Paper
        elevation={6}
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 3,
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(12px)",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          height: { xs: "auto", md: "80vh" },
          width: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {processedImage ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={processedImage}
              alt="Processed"
              style={{
                maxWidth: "100%",
                maxHeight: "calc(100% - 80px)",
                borderRadius: "12px",
                transition: "0.3s ease",
                boxShadow: "0px 5px 20px rgba(255, 255, 255, 0.1)",
              }}
            />

            {/* Scanning Overlay */}
            {scanning && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "110%",
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                {/* Horizontal scanning line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    width: "100%",
                    height: "3px",
                    background:
                      "linear-gradient(90deg, transparent, #EDB38F, #F98995, #A41847, transparent)",
                    boxShadow:
                      "0 0 10px #EDB38F, 0 0 20px #F98995, 0 0 30px #A41847",
                    animation: "scanHorizontal 2s ease-in-out infinite",
                    "@keyframes scanHorizontal": {
                      "0%": { top: "10%", opacity: 0 },
                      "15%": { opacity: 1 },
                      "85%": { opacity: 1 },
                      "100%": { top: "90%", opacity: 0 },
                    },
                  }}
                />
                {/* Vertical scanning line */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    height: "100%",
                    width: "3px",
                    background:
                      "linear-gradient(0deg, transparent, #EDB38F, #F98995, #A41847, transparent)",
                    boxShadow:
                      "0 0 10px #EDB38F, 0 0 20px #F98995, 0 0 30px #A41847",
                    animation: "scanVertical 2s ease-in-out infinite",
                    animationDelay: "1s",
                    "@keyframes scanVertical": {
                      "0%": { left: "10%", opacity: 0 },
                      "15%": { opacity: 1 },
                      "85%": { opacity: 1 },
                      "100%": { left: "90%", opacity: 0 },
                    },
                  }}
                />
                {/* Pulsing Overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background:
                      "radial-gradient(circle, transparent, rgba(237, 179, 143, 0.05))",
                    animation: "pulse 1.5s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%": { opacity: 0.1 },
                      "50%": { opacity: 0.3 },
                      "100%": { opacity: 0.1 },
                    },
                  }}
                />
              </Box>
            )}

            {/* Fixed Status Area Below Image */}
            <Box sx={{ mt: 2, textAlign: "center" }}>
              {scanning ? (
                <Typography
                  sx={{
                    color: "#F98995",
                    fontSize: { xs: 16, md: 18 },
                    fontWeight: "bold",
                  }}
                >
                  Beautifying in progress...
                  <br />
                  Getting your glow-up started!
                </Typography>
              ) : (
                <a
                  href={processedImage}
                  download="processed_eyeliner_image.png"
                >
                  <Button
                    variant="contained"
                    sx={{
                      ...commonButtonStyle,
                      background: "#a41847",
                      "&:hover": { background: "#860f37" },
                    }}
                  >
                    Download Image
                  </Button>
                </a>
              )}
            </Box>
          </Box>
        ) : (
          <Typography sx={{ color: "#ccc", fontSize: 18, textAlign: "center" }}>
            {scanning
              ? "Scanning image..."
              : "Upload an image to apply eyeliner"}
          </Typography>
        )}
      </Paper>

      {/* Controls */}
      <Paper
        elevation={6}
        sx={{
          p: 4,
          borderRadius: 3,
          background: "rgba(255, 255, 255, 0.05)",
          backdropFilter: "blur(12px)",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
          height: { xs: "auto", md: "auto" },
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 3,
          color: "#fff",
        }}
      >
        {/* Image Selection Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: { xs: "center", md: "flex-start" },
          }}
        >
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "uploaded");
            }}
          >
            Uploaded Images
          </Button>
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "captured");
            }}
          >
            Captured Images
          </Button>
          <Button
            variant="contained"
            startIcon={<ImageIcon />}
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
            }}
            onClick={() => {
              setImageBrowserOpen(true);
              localStorage.setItem("imageType", "model");
            }}
          >
            Model Image
          </Button>
        </Box>

        {/* Wing Scale */}
        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Wing Scale
        </Typography>
        <Slider
          value={wingScale}
          min={0}
          max={100}
          onChange={(e, newValue) => setWingScale(Number(newValue))}
          sx={{ color: "#a41847", height: 6 }}
        />

        {/* Fill Intensity */}
        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Fill Intensity
        </Typography>
        <Slider
          value={fillIntensity}
          min={0}
          max={100}
          onChange={(e, newValue) => setFillIntensity(Number(newValue))}
          sx={{ color: "#a41847", height: 6 }}
        />

        {/* Skip Fraction */}
        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Skip Fraction
        </Typography>
        <Slider
          value={skipFraction}
          min={0}
          max={50}
          onChange={(e, newValue) => setSkipFraction(Number(newValue))}
          sx={{ color: "#a41847", height: 6 }}
        />

        {/* Eyeliner Color Picker */}
        <Typography sx={{ fontSize: 18, fontWeight: "bold" }}>
          Eyeliner Color
        </Typography>
        <Box>
          <input
            type="color"
            onChange={(e) => {
              const hex = e.target.value;
              setColor({
                r: parseInt(hex.slice(1, 3), 16),
                g: parseInt(hex.slice(3, 5), 16),
                b: parseInt(hex.slice(5, 7), 16),
              });
            }}
            style={{
              display: "block",
              width: "100%",
              height: 50,
              border: "none",
              cursor: "pointer",
              borderRadius: "6px",
              background: "none",
              boxShadow: "0px 3px 12px rgba(255, 255, 255, 0.1)",
            }}
          />
        </Box>

        {/* Apply Makeup Button */}
        <Box sx={{ mt: "auto" }}>
          <Button
            variant="contained"
            sx={{
              ...commonButtonStyle,
              background: "#a41847",
              "&:hover": { background: "#860f37" },
              width: { xs: "100%", md: "auto" },
              mx: "auto", // centers horizontally if width < 100%
              display: "block",
            }}
            onClick={() => processImage(image)}
          >
            Apply Makeup
          </Button>
        </Box>
      </Paper>
      {/* Toast container for notifications */}
      <ToastContainer />
    </Box>
  );
}
